import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from "react-redux";
import Swal from 'sweetalert2';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getCategoryAPI, updateCategoryAPI } from '../../services/api/CategoriesAPI';
import Layout from '../../layouts/Layout';
import { FaStarOfLife } from 'react-icons/fa';
import Loader from '../components/Loader';

const EditCategory = () => {
    const { id } = useParams();
    const user = useSelector((state) => state.LoginReducer.authData);
    const token = user.data.token;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const validationSchema = Yup.object({
        name: Yup.string()
            .min(3, 'Name must be at least 3 characters long')
            .required('Name is required'),
        description: Yup.string()
            .required('Description is required')
            .max(500, 'Description must be 500 characters or less'),
        image: Yup.mixed()
            .nullable()
            .test('fileType', 'Only JPEG and PNG files are allowed', (value) => {
                if (value) {
                    return value && (value.type === 'image/jpeg' || value.type === 'image/png');
                }
                return true;
            })
            .test('fileSize', 'File size exceeds the 2MB limit', (value) => {
                if (value) {
                    return value.size <= 2 * 1024 * 1024;
                }
                return true;
            }),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
            image: null,
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const formData = new FormData();
            formData.append('name', values.name);
            formData.append('description', values.description);
            formData.append('image', values.image);
            await updateCategory(formData);
        },
    });

    useEffect(() => {
        const fetchCategory = async () => {
            try {
                const data = await getCategoryAPI(id, token);
                if (data === null) {
                    navigate('/404');
                } else {
                    formik.setValues({
                        name: data.name,
                        description: data.description,
                        image: null,
                        imagePreview: data.image,
                    });
                }                  
                
            } catch (error) {
                Swal.fire({
                    title: 'Error!',
                    text: 'Could not fetch category data.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        };

        fetchCategory();
    }, [id]);

    // Handle image change
    const handleImageChange = (event) => {
        const file = event.currentTarget.files[0];
        formik.setFieldValue('image', file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                formik.setFieldValue('imagePreview', reader.result);  // Add a preview state if necessary
            };
            reader.readAsDataURL(file);
        }
    };

    const updateCategory = async (formData) => {
        try {
            const data = await updateCategoryAPI(formData, id, token);
            setLoading(false);
            if (data.status) {
                Swal.fire({
                    title: 'Category Updated!',
                    text: 'The category has been updated successfully.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    navigate('/categories');
                });
            }
        } catch (error) {
            setLoading(false);
            Swal.fire({
                title: 'Error!',
                text: 'Something went wrong please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Layout title="Edit Category">
            <div className="container table-container">
                <form onSubmit={formik.handleSubmit}>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-md-12 col-12">
                                <div className="form-group py-3 required">
                                    <label htmlFor="name">Categories Name <FaStarOfLife className='text-danger' /></label>
                                    <input type="text" className="form-control mt-1" name="name" id="name" placeholder="Enter Categories name" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.name} />
                                </div>
                                {formik.touched.name && formik.errors.name ? ( <label className="text-danger">{formik.errors.name}</label>) : null}
                            </div>
                            <div className="col-md-12 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="description">Description <FaStarOfLife className='text-danger' /></label>
                                    <textarea className="form-control mt-1" rows={3} name="description" id="description" placeholder='Description' onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.description}>{formik.values.description}</textarea>
                                </div>
                                {formik.touched.description && formik.errors.description ? ( <label className="text-danger">{formik.errors.description}</label>) : null}
                            </div>
                            <div className="col-md-6 col-12">
                                <div className="form-group py-2 required">
                                    <label htmlFor="image">Categories Image <FaStarOfLife className='text-danger' /></label>
                                    <input type="file" className="form-control mt-1" name="image" id="image" accept="image/*" onChange={handleImageChange} />
                                </div>
                                {formik.touched.image && formik.errors.image ? ( <label className="text-danger">{formik.errors.image}</label>) : null}
                            </div>
                            {formik.values.imagePreview && (
                                <div className="col-md-6">
                                    <img className='image-preview' src={formik.values.imagePreview} alt="Image Preview" />
                                </div>
                            )}
                        </div>
                        <div className="row mt-3">
                            <div className="col-md-6 col-12 d-flex gap-3">
                                <Link to={'/categories'} type="submit" className="btn btn-dark cancel_btn">Cancel</Link>
                                <button type="submit" className="btn btn-primary submit_btn">Submit</button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Layout>
    )
}

export default EditCategory;